/**
 * Analytics Constants — barrel re-export
 *
 * Usage:
 * ```typescript
 * import { ANALYTICS_FEATURES, ANALYTICS_ACTIONS, ANALYTICS_PARAMS } from '@/constants/analytics';
 * trackFeature(ANALYTICS_FEATURES.AUTH, ANALYTICS_ACTIONS.AUTH.LOGIN_SUCCESS, { ... });
 * ```
 */

export { ANALYTICS_FEATURES } from './features';
export type { AnalyticsFeature } from './features';

export { ANALYTICS_PARAMS } from './params';
export type { AnalyticsParamKey } from './params';

// Action group imports
import { COMMON_ACTIONS } from './actions/common';
import { AUTH_ACTIONS } from './actions/auth';
import { PROJECTS_ACTIONS, PROJECT_DETAIL_ACTIONS } from './actions/projects';
import { PORTFOLIO_ACTIONS } from './actions/portfolio';
import { DATAROOM_ACTIONS } from './actions/dataroom';
import { INTELLIGENCE_ACTIONS } from './actions/intelligence';
import { PUBLIC_ACTIONS } from './actions/public';
import { ADMIN_ACTIONS } from './actions/admin';
import { BLOG_ACTIONS, CHATBOT_ACTIONS, NOTES_ACTIONS, PDF_ACTIONS, DOCUMENT_ACTIONS } from './actions/content';
import { CONTACT_ACTIONS, DEMO_ACTIONS } from './actions/contact';
import { PROFILE_ACTIONS, ORGANIZATION_ACTIONS } from './actions/user';
import { EDITING_ACTIONS } from './actions/editing';
import { MFA_ACTIONS } from './actions/mfa';
import { RISK_MONITOR_ACTIONS } from './actions/riskMonitor';
import { DOCUMENT_STUDIO_ACTIONS } from './actions/documentStudio';
import { WORKSHOP_ACTIONS } from './actions/workshop';

/**
 * All analytics actions organized by domain
 */
export const ANALYTICS_ACTIONS = {
  COMMON: COMMON_ACTIONS,
  AUTH: AUTH_ACTIONS,
  MFA: MFA_ACTIONS,
  PROJECTS: PROJECTS_ACTIONS,
  PROJECT_DETAIL: PROJECT_DETAIL_ACTIONS,
  PORTFOLIO: PORTFOLIO_ACTIONS,
  DATAROOM: DATAROOM_ACTIONS,
  INTELLIGENCE: INTELLIGENCE_ACTIONS,
  PUBLIC: PUBLIC_ACTIONS,
  ADMIN: ADMIN_ACTIONS,
  BLOG: BLOG_ACTIONS,
  CHATBOT: CHATBOT_ACTIONS,
  NOTES: NOTES_ACTIONS,
  PDF: PDF_ACTIONS,
  DOCUMENT: DOCUMENT_ACTIONS,
  CONTACT: CONTACT_ACTIONS,
  DEMO: DEMO_ACTIONS,
  PROFILE: PROFILE_ACTIONS,
  ORGANIZATION: ORGANIZATION_ACTIONS,
  EDITING: EDITING_ACTIONS,
  RISK_MONITOR: RISK_MONITOR_ACTIONS,
  DOCUMENT_STUDIO: DOCUMENT_STUDIO_ACTIONS,
  WORKSHOP: WORKSHOP_ACTIONS,
} as const;

// Derive union type of all action string values for type safety
type ActionValues<T> = T extends Record<string, infer V>
  ? V extends Record<string, string>
    ? V[keyof V]
    : V extends string
      ? V
      : never
  : never;

export type AnalyticsAction = ActionValues<typeof ANALYTICS_ACTIONS>;
